import dayjs from "dayjs";
import { DEFAULT_SURPLUS_DATA, DEFAULT_SURPLUS_TIME, SURPLUS_STATUS, TYPE_OF_ATTRIBUTE } from "../../constants/surplus";
import { calculateCostPrice } from "./productMapping";
import { formatTime } from "./helper";
import { changeTimeFormatIntoDayjsFormat } from "./formatDate";

// For post and put data
export const getBodyForSurplusProduct = (values: CreateUpdateSurplusProductValues): CreateUpdateSurplusProductBody => {
  return {
    ...DEFAULT_SURPLUS_DATA,
    ...values,
    main_tags: null,
    category_id: Number(values.category_id),
    shipment_weight: Number(values.shipment_weight),
    width: Number(values.width),
    height: Number(values.height),
    depth: Number(values.depth),
    condition: Number(values.condition),
    status: values.status as number,
    day_to_prepare_order: Number(values.day_to_prepare_order),
    lead_time: Number(values.lead_time),
    startTime: values.startTime ? formatTime(values.startTime as string) : DEFAULT_SURPLUS_TIME.START,
    endTime: values.endTime ? formatTime(values.endTime as string) : DEFAULT_SURPLUS_TIME.END,
    discountPercentage: values.discountPercentage,
    availableQuantity: Number(values.availableQuantity),
    price: Number(values.price),
    sellingPrice: Number(values.sellingPrice),
    costPrice: Number(values.costPrice),
    attributes: getBodyAttributesForSurplus(values),
  };
};

export const getBodyAttributesForSurplus = (values: CreateUpdateSurplusProductValues): SurplusProductAttribute[] => {
  const attributes: SurplusProductAttribute[] = values.attributes.map(item => ({
    ...item,
    attribute: item.attribute_name,
    isRequired: false, //hard code, remove later
    isMultipleChoice: true,
    variants: item.variants
      .filter(item => item)
      .map(variant => ({
        ...variant,
        variant: variant.attribute_value,
        price: Number(variant.price),
        maximumAllowQuantity: Number(variant.maximumAllowQuantity || 1),
      })),
    min: Number(item.min),
    max: Number(item.max),
    quantifiable: item.quantifiable ? item.quantifiable : false,
    type_of_attribute: TYPE_OF_ATTRIBUTE.isMultipleChoice,
  }));
  const requiredAttribute = attributes.find(item => item.isRequired);
  const defaultAttribute: SurplusProductAttribute[] = [
    {
      attribute_name: "default",
      attribute: "default",
      isRequired: true,
      isMultipleChoice: false,
      type_of_attribute: TYPE_OF_ATTRIBUTE.isRequired,
      variants: [
        {
          attribute_value: "default",
          variant: "default",
          price: Number(values.sellingPrice),
          isSoldOut: false,
        },
      ],
      min: 1,
      max: 1,
      quantifiable: false,
    },
  ];
  return [...attributes, ...(requiredAttribute ? [] : defaultAttribute)];
};

export const isVariantSurplusDefault = (attribute: SurplusProductAttribute): boolean => {
  return !!(
    attribute.attribute === "default" &&
    attribute.isRequired &&
    attribute.variants.length === 1 &&
    attribute.variants[0]?.variant === "default"
  );
};

export const getVariantSurplusDefault = (surplus: Surplus): SurplusProductVariation | undefined => {
  return surplus?.attributes?.find(attribute => isVariantSurplusDefault(attribute))?.variants[0];
};

export const getNormalSurplusAttributes = (
  { attributes }: { attributes: Surplus["attributes"] },
  markup: number
): SurplusProductAttribute[] => {
  if (!attributes) return [];

  return attributes
    .filter(attribute => !isVariantSurplusDefault(attribute))
    .map(item => {
      return {
        ...item,
        variants: item.variants.map(item => {
          return {
            ...item,
            price: item.price ? Number(item.price).toFixed(2) : "",
            costPrice: item.price ? calculateCostPrice(item.price, markup) : "",
          };
        }),
      };
    });
};

export const getAttributesValueFromSurplusProductDetail = (
  markup: number,
  defaultAttributes: Attribute[] = [],
  surplus?: Surplus | null
): { price?: string; quantity?: string; attributes: any } => {
  if (!surplus) {
    return {
      price: "",
      attributes: defaultAttributes.map(item => ({
        ...item,
        quantifiable: false,
        attribute_name: item.name,
        variants: [{ variant: "", price: "" }],
        type_of_attribute: item.is_required
          ? TYPE_OF_ATTRIBUTE.isRequired
          : item.is_multiple_choice
          ? TYPE_OF_ATTRIBUTE.isMultipleChoice
          : "",
      })),
    };
  }
  const defaultVariant = getVariantSurplusDefault(surplus);
  const attributes = getNormalSurplusAttributes(surplus, markup);

  return {
    price: defaultVariant?.price !== undefined ? defaultVariant.price.toString() : "",
    attributes: attributes.map(attribute => ({
      ...attribute,
      attribute_name: attribute.attribute,
      type_of_attribute: attribute.isRequired
        ? TYPE_OF_ATTRIBUTE.isRequired
        : attribute.isMultipleChoice
        ? TYPE_OF_ATTRIBUTE.isMultipleChoice
        : "",
      variants:
        attribute.variants && attribute.variants.length > 0
          ? attribute.variants.map(variant => ({
              ...variant,
              attribute_value: variant.variant,
            }))
          : [],
    })),
  };
};

// For filling data into form
export const getValuesFromSurplusProductDetail = (
  markup: number,
  surplus?: Surplus | null,
  merchantStoreId?: string | number,
  defaultAttributes?: Attribute[]
): CreateUpdateSurplusProductValues => {
  defaultAttributes?.sort((a, b) => a.id - b.id);

  const { price, attributes } = getAttributesValueFromSurplusProductDetail(
    markup,
    surplus?.id ? [] : defaultAttributes,
    surplus
  );

  return {
    ...DEFAULT_SURPLUS_DATA,
    ...surplus,
    id: String(surplus?.id) || "",
    canPreOrder: surplus?.canPreOrder || false,
    price: Number(price).toFixed(2) || "",
    costPrice: surplus?.costPrice ? Number(surplus?.costPrice || 0).toFixed(2) : "",
    sellingPrice: surplus?.sellingPrice ? Number(surplus?.sellingPrice || 0).toFixed(2) : "",
    attributes: attributes,
    startTime: surplus?.startTime ? changeTimeFormatIntoDayjsFormat(surplus?.startTime) : null,
    endTime: surplus?.endTime ? changeTimeFormatIntoDayjsFormat(surplus?.endTime) : null,
    // From old product model
    merchant_store_id: merchantStoreId?.toString() || surplus?.merchant_store_id || "",
    groupMenuId: surplus?.groupMenuId || "",
    category_id: surplus?.category_id?.toString() || null,
    product_warranty: (surplus?.warranty as PRODUCT_WARRANTY) || "",
    width: surplus?.width?.toString() || "",
    height: surplus?.height?.toString() || "",
    depth: surplus?.depth?.toString() || "",
    condition: surplus?.condition?.toString() || "",
    day_to_prepare_order: surplus?.day_to_prepare_order?.toString() || "",
    lead_time: surplus?.lead_time?.toString() || "",
    status: surplus?.status?.toString() || "",
    main_tags: null,
    shipment_weight: surplus?.shipment_weight?.toString() || undefined,
  };
};

export const createNewSurplusAttribute = (): SurplusProductAttribute => ({
  attribute_name: "",
  attribute: "",
  type_of_attribute: TYPE_OF_ATTRIBUTE.isMultipleChoice,
  isRequired: false,
  max: 1,
  min: 0,
  variants: [
    {
      attribute_value: "",
      variant: "",
      price: "",
      isSoldOut: false,
    },
  ],
  quantifiable: false,
});

/**
 * This function used for mapping from API to component
 * @param data
 * @returns
 */
export const parseDataFromDetailToStandardVariableData = (data: SurplusDataFromApi | null): Surplus | null => {
  if (!data) return null;

  return {
    ...DEFAULT_SURPLUS_DATA,
    ...data,
    costPrice: Number(data.costPrice || 0),
    sellingPrice: Number(data.sellingPrice || 0),
    discountPercentage: Number(data.discountPercentage),
    attributes: data.attributes,
    merchant_store_id: data.storeId,
    images: [data.imageUrl],
    startTime: data.startTime || DEFAULT_SURPLUS_TIME.START,
    endTime: data.endTime || DEFAULT_SURPLUS_TIME.END,
    isSoldOut: data.status === SURPLUS_STATUS.SOLD_OUT || false,
  };
};

export const mappingSurplusAttributeWithOldAttributeAfterEdit = (
  oldAttributes: SurplusProductAttribute[],
  newAttributes: SurplusProductAttribute[]
): SurplusProductAttribute[] => {
  const finalAttributes: SurplusProductAttribute[] = [];

  const getAttrById = (attrs: SurplusProductAttribute[], id?: string) =>
    id ? attrs.find(attr => attr.id === id) : undefined;

  // Check for updated or deleted attributes
  oldAttributes.forEach(oldAttr => {
    const matchedAttr = getAttrById(newAttributes, oldAttr.id);

    if (!matchedAttr) {
      // Old attribute not found → deleted
      return;
    } else {
      // Attribute exists
      const updatedVariants: SurplusProductVariation[] = [];

      oldAttr.variants.forEach(oldVariant => {
        const matchedVar = matchedAttr.variants.find(v => v.id === oldVariant.id);
        if (!matchedVar) {
          // Variant was removed → deleted
          return;
        } else {
          // Variant exists → update it
          updatedVariants.push({ ...oldVariant, ...matchedVar });
        }
      });

      // Find new variants (no ID = newly added)
      matchedAttr.variants.filter(v => !v.id).forEach(newVariant => updatedVariants.push(newVariant));

      finalAttributes.push({
        ...oldAttr,
        ...matchedAttr,
        variants: updatedVariants,
      });
    }
  });

  // New attributes (no ID = newly created)
  newAttributes.filter(attr => !attr.id).forEach(newAttr => finalAttributes.push(newAttr));

  return finalAttributes;
};
